import { useCallback, useEffect, useRef, useState } from 'react';
import { PaperPlaneRightIcon, TrashIcon, XIcon } from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import type { AnnotationIntent, AnnotationStroke } from '@erp/rpc';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { ToggleGroup, ToggleGroupItem } from 'src/components/ui/toggle-group';
import { cn } from 'src/lib/utils';

/**
 * Annotate-mode capture surface (AI-246), rendered over the (inert) live-view iframe while the
 * interview is paused at a question. The user draws freehand marks and tags each with an intent;
 * on send, the strokes go up as VECTORS in normalized [0,1] coords (relative to this canvas, which
 * matches the iframe rect) — the ai-service resolves each to an @eN ref and feeds the agent the
 * guidance plus a fresh screenshot.
 *
 * Plain canvas + pointer events rather than a drawing lib: a few lines do it, and we own the
 * coordinate space (normalization fidelity matters for ref resolution).
 *
 * ponytail: assumes the live stream fills the iframe without letterboxing, so normalized→viewport
 * scaling is linear. The live-Kernel spike validates that; until then the resolved ref is a HINT to
 * the model (it also gets a fresh screenshot), so a slight mis-scale degrades, it doesn't mislead.
 */

const INTENT_COLORS: Record<AnnotationIntent, string> = {
  act_here: '#10b981', // emerald
  avoid: '#ef4444', // red
  note: '#f59e0b', // amber
};

interface DrawnStroke {
  intent: AnnotationIntent;
  points: { x: number; y: number }[]; // normalized [0,1]
}

interface InterviewAnnotateOverlayProps {
  onSubmit: (annotation: {
    note?: string;
    strokes: AnnotationStroke[];
  }) => void;
  onCancel: () => void;
  pending: boolean;
}

export function InterviewAnnotateOverlay({
  onSubmit,
  onCancel,
  pending,
}: InterviewAnnotateOverlayProps) {
  const gt = useGT();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [intent, setIntent] = useState<AnnotationIntent>('act_here');
  const [strokes, setStrokes] = useState<DrawnStroke[]>([]);
  const [note, setNote] = useState('');
  const drawing = useRef<DrawnStroke | null>(null);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.lineWidth = 3;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    const all = drawing.current ? [...strokes, drawing.current] : strokes;
    for (const stroke of all) {
      if (stroke.points.length === 0) continue;
      ctx.strokeStyle = INTENT_COLORS[stroke.intent];
      ctx.beginPath();
      stroke.points.forEach((p, i) => {
        const x = p.x * canvas.width;
        const y = p.y * canvas.height;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.stroke();
    }
  }, [strokes]);

  // Keep the canvas backing store sized to its rendered (CSS) box so 1 canvas px = 1 CSS px and
  // normalization is exact, then repaint. Re-runs on a resize (ResizeObserver) or a strokes change
  // (redraw identity); live in-progress strokes repaint directly from the pointer handlers.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const sync = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = Math.max(1, Math.round(rect.width));
      canvas.height = Math.max(1, Math.round(rect.height));
      redraw();
    };
    sync();
    const observer = new ResizeObserver(sync);
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [redraw]);

  const toNormalized = (e: React.PointerEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height)),
    };
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    drawing.current = { intent, points: [toNormalized(e)] };
    redraw();
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    drawing.current.points.push(toNormalized(e));
    redraw();
  };

  const finishStroke = () => {
    const stroke = drawing.current;
    drawing.current = null;
    if (stroke && stroke.points.length > 0) {
      setStrokes((prev) => [...prev, stroke]);
    }
  };

  const canSend = strokes.length > 0 || note.trim().length > 0;

  const handleSend = () => {
    if (!canSend || pending) return;
    onSubmit({
      note: note.trim() || undefined,
      strokes: strokes.map((s) => ({
        intent: s.intent,
        kind: 'freehand',
        points: s.points,
      })),
    });
  };

  return (
    <div className="absolute inset-0">
      {/* Floating toolbar — sits above the canvas; its own pointer events don't draw. */}
      <div className="absolute left-2 right-2 top-2 z-10 flex flex-wrap items-center gap-2 rounded-md bg-background/90 p-2 shadow-md backdrop-blur">
        <ToggleGroup
          type="single"
          value={intent}
          onValueChange={(v) => v && setIntent(v as AnnotationIntent)}
          size="sm"
        >
          <ToggleGroupItem value="act_here">
            <span
              className="mr-1.5 inline-block h-2.5 w-2.5 rounded-full"
              style={{ backgroundColor: INTENT_COLORS.act_here }}
            />
            <T context="Annotation intent: where the agent should act">
              Act here
            </T>
          </ToggleGroupItem>
          <ToggleGroupItem value="avoid">
            <span
              className="mr-1.5 inline-block h-2.5 w-2.5 rounded-full"
              style={{ backgroundColor: INTENT_COLORS.avoid }}
            />
            <T context="Annotation intent: region to avoid">Avoid</T>
          </ToggleGroupItem>
          <ToggleGroupItem value="note">
            <span
              className="mr-1.5 inline-block h-2.5 w-2.5 rounded-full"
              style={{ backgroundColor: INTENT_COLORS.note }}
            />
            <T context="Annotation intent: a neutral note">Note</T>
          </ToggleGroupItem>
        </ToggleGroup>

        <Input
          className="h-8 flex-1 min-w-[8rem]"
          placeholder={gt('Add a note (optional)')}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />

        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            drawing.current = null;
            setStrokes([]);
            redraw();
          }}
          disabled={strokes.length === 0}
        >
          <TrashIcon className="mr-1 h-3.5 w-3.5" />
          <T context="Clear drawn annotations">Clear</T>
        </Button>
        <Button size="sm" onClick={handleSend} disabled={!canSend || pending}>
          <PaperPlaneRightIcon className="mr-1 h-3.5 w-3.5" />
          <T context="Send the annotation to the agent">Send</T>
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={onCancel}
        >
          <XIcon className="h-4 w-4" />
        </Button>
      </div>

      <canvas
        ref={canvasRef}
        className={cn(
          'h-full w-full',
          pending ? 'cursor-wait' : 'cursor-crosshair',
        )}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={finishStroke}
        onPointerLeave={finishStroke}
      />
    </div>
  );
}
